import { create } from 'zustand';
import { ModelStatusesApi, type ModelStatus, type UpsertModelStatusParams } from '../services/api/model-statuses-api';

const api = new ModelStatusesApi();

/** Predefined status type definitions with default text and colors */
export const STATUS_TYPE_CONFIG: Record<string, {
  label: string;
  defaultText: string;
  textColor: string;
  bgColor: string;
  defaultSeverity: 'info' | 'warning' | 'critical';
}> = {
  high_load: {
    label: 'Высокая нагрузка',
    defaultText: 'Нагрузка',
    textColor: '#000000',
    bgColor: '#FFAA33', // Bright Amber
    defaultSeverity: 'warning',
  },
  slow: {
    label: 'Долгое ожидание',
    defaultText: 'Ожидание',
    textColor: '#000000',
    bgColor: '#FFD60A', // Bright Yellow
    defaultSeverity: 'warning',
  },
  unstable: {
    label: 'Нестабильная',
    defaultText: 'Нестабильна',
    textColor: '#FFFFFF',
    bgColor: '#FF6B6B', // Soft Neon Red
    defaultSeverity: 'critical',
  },
  maintenance: {
    label: 'На обслуживании',
    defaultText: 'Обслуживание',
    textColor: '#FFFFFF',
    bgColor: '#A1A1AA', // Zinc-400
    defaultSeverity: 'critical',
  },
  new: {
    label: 'Новинка',
    defaultText: 'NEW',
    textColor: '#FFFFFF',
    bgColor: '#FF5C8D', // Neon Pink
    defaultSeverity: 'info',
  },
  beta: {
    label: 'Бета',
    defaultText: 'BETA',
    textColor: '#FFFFFF',
    bgColor: '#C084FC', // Bright Purple
    defaultSeverity: 'info',
  },
  recommended: {
    label: 'Рекомендуем',
    defaultText: 'TOP',
    textColor: '#000000',
    bgColor: '#85FF00', // Brand Lime
    defaultSeverity: 'info',
  },
  limited: {
    label: 'Ограничена',
    defaultText: 'Лимит',
    textColor: '#000000',
    bgColor: '#FFAA33', // Bright Amber
    defaultSeverity: 'warning',
  },
  exclusive: {
    label: 'Эксклюзив',
    defaultText: 'EXCLUSIVE',
    textColor: '#000000',
    bgColor: '#85FF00', // Brand Lime
    defaultSeverity: 'info',
  },
  custom: {
    label: 'Свой текст',
    defaultText: '',
    textColor: '#FFFFFF',
    bgColor: '#60A5FA', // Bright Blue
    defaultSeverity: 'info',
  },
};

/** Resolved display info for a status */
export interface ResolvedStatus {
  id: number;
  modelId: string;
  modelType: 'chat' | 'image' | 'video';
  statusType: string;
  text: string;
  textColor: string;
  bgColor: string;
  severity: 'info' | 'warning' | 'critical';
  showInSelector: boolean;
  showInComposer: boolean;
  expiresAt: string | null;
}

interface ModelStatusesState {
  statuses: ModelStatus[];
  isLoading: boolean;
  lastFetch: number;

  /** Fetch active statuses from the public endpoint */
  fetchStatuses: () => Promise<void>;

  /** Get resolved status for a specific model (returns null if no status) */
  getStatus: (modelId: string, modelType: 'chat' | 'image' | 'video') => ResolvedStatus | null;

  /** Check if model is under maintenance (should be blocked) */
  isModelUnderMaintenance: (modelId: string, modelType: 'chat' | 'image' | 'video') => boolean;

  /** Get all models with 'recommended' status for a given type */
  getRecommendedModels: (modelType: 'chat' | 'image' | 'video') => string[];

  /** Admin: create or update a status */
  upsertStatus: (params: UpsertModelStatusParams) => Promise<ModelStatus | null>;

  /** Admin: delete a status */
  deleteStatus: (id: number) => Promise<void>;
}

export const useModelStatusesStore = create<ModelStatusesState>((set, get) => ({
  statuses: [],
  isLoading: false,
  lastFetch: 0,

  fetchStatuses: async () => {
    // Throttle: don't fetch more often than every 30 seconds
    const now = Date.now();
    if (now - get().lastFetch < 30000 && get().statuses.length > 0) return;

    set({ isLoading: true });
    try {
      const statuses = await api.getActiveStatuses();
      set({ statuses, lastFetch: Date.now() });
    } catch {
      // Keep existing statuses on error
    } finally {
      set({ isLoading: false });
    }
  },

  getStatus: (modelId: string, modelType: 'chat' | 'image' | 'video'): ResolvedStatus | null => {
    const { statuses } = get();
    const status = statuses.find(s => s.modelId === modelId && s.modelType === modelType && s.isActive);
    if (!status) return null;

    const typeConfig = STATUS_TYPE_CONFIG[status.statusType] || STATUS_TYPE_CONFIG.custom;

    return {
      id: status.id,
      modelId: status.modelId,
      modelType: status.modelType,
      statusType: status.statusType,
      text: status.customText || typeConfig.defaultText,
      textColor: typeConfig.textColor,
      bgColor: typeConfig.bgColor,
      severity: status.severity,
      showInSelector: status.showInSelector,
      showInComposer: status.showInComposer,
      expiresAt: status.expiresAt,
    };
  },

  isModelUnderMaintenance: (modelId: string, modelType: 'chat' | 'image' | 'video'): boolean => {
    const { statuses } = get();
    return statuses.some(s =>
      s.modelId === modelId &&
      s.modelType === modelType &&
      s.isActive &&
      s.statusType === 'maintenance'
    );
  },

  getRecommendedModels: (modelType: 'chat' | 'image' | 'video'): string[] => {
    const { statuses } = get();
    return statuses
      .filter(s => s.modelType === modelType && s.isActive && s.statusType === 'recommended')
      .map(s => s.modelId);
  },

  upsertStatus: async (params: UpsertModelStatusParams) => {
    const result = await api.upsertStatus(params);
    if (result) {
      // Refresh statuses after mutation
      set({ lastFetch: 0 });
      await get().fetchStatuses();
    }
    return result;
  },

  deleteStatus: async (id: number) => {
    await api.deleteStatus(id);
    // Refresh statuses after mutation
    set({ lastFetch: 0 });
    await get().fetchStatuses();
  },
}));
